import { PropertyType } from '@devicepilot/manifest-types';
import Color from 'color';
import L from 'leaflet';
import { getBoundaryColour, getColourInRange, getHslColourInRange, getStringHashColour } from '@/helpers/colour';
import { delay } from '@/helpers/promises';
import store from '@/store';
import { PALETTE_COLOURS } from '@/store/types/getters';
import { COLOURS } from '@/store/types/namespaces';

export const strokeWidth = 2;
const palette = `${COLOURS}/${PALETTE_COLOURS}`;

function getEmptyColour() {
  return store.getters[palette].neutral[5];
}

export function getMarkerIcon(args, isSelected) {
  if (!args || typeof args !== 'object') {
    return null;
  }

  const {
    id, colour, opacity, icon, text, darkMode,
  } = args;
  const defaultMarkerColour = store.getters[palette].primary;

  let strokeColour = '#888';
  if (colour) {
    strokeColour = Color(colour).darken(0.2).string();
  } else {
    strokeColour = Color(defaultMarkerColour).darken(0.2).string();
  }

  let style = `
    background: ${colour || defaultMarkerColour};
    opacity: ${opacity || 1};
    border: ${strokeWidth}px solid ${strokeColour};`;

  if (isSelected === true) {
    const selectColour = darkMode ? '#fff' : '#222';
    style += `box-shadow: 0 0 0 4px ${selectColour};`;
  }

  const getContent = () => {
    if (icon) {
      return `<span class="fas fa-${icon}"></span>`;
    } if (text) {
      return `<span>${text}</span>`;
    }

    return '';
  };

  const htmlIcon = `<div class="dp-map-marker" id="${id}" style="${style}">${getContent()}</div>`;
  const divIcon = L.divIcon({
    html: htmlIcon,
    class: 'svg-icon',
    iconAnchor: [13, 32],
    iconSize: [26, 26],
    popupAnchor: [0, -25],
  });

  return divIcon;
}

export function generateMarker(datum, args) {
  const data = datum || {};
  const { device, title } = data;
  const {
    useColour,
    colourProperty,
    maximumColourValue,
    minimumColourValue,
    startColour,
    endColour,
    darkMode,
  } = args;
  let { tooltip } = data;

  const { latitude, longitude } = device;

  const opts = data;
  opts.id = device.$id;
  opts.icon = device.icon;
  opts.darkMode = darkMode;

  const useHsl = startColour && startColour.substring(0, 3) === 'hsl';

  const isKpi = colourProperty && colourProperty.name === '$kpi';

  // Add colouring
  if ((useColour && colourProperty) || isKpi) {
    const value = device[colourProperty.name];
    const strValue = isKpi ? value.value : value;
    opts.value = value;

    tooltip += `<br><strong>${colourProperty.alias}</strong>: ${strValue}`;

    if (isKpi) {
      const val = opts.value || {};
      const { state } = val;
      if (state) {
        opts.colour = store.getters[palette][state];
      } else {
        opts.colour = store.getters[palette].primary;
      }
    } else if (value === undefined || value === null) {
      opts.opacity = 0.3;
      opts.colour = getEmptyColour();
    } else if (colourProperty.type === PropertyType.String || colourProperty.isEnumerated) {
      opts.colour = getStringHashColour(value, store.getters[palette].chart);
    } else if (colourProperty.type === PropertyType.Number) {
      const { error, success } = store.getters[palette];

      // Catch the case of a non-numeric value
      if (Number.isNaN(value)) {
        opts.opacity = 0.3;
        opts.colour = getEmptyColour();
      } else if (useHsl) {
        const parsedStart = parseInt(startColour.substring(4), 10);
        const parsedEnd = parseInt(endColour.substring(4), 10);
        const startHue = Number.isNaN(parsedStart) ? 0 : parsedStart;
        const endHue = Number.isNaN(parsedEnd) ? 240 : parsedEnd;

        opts.colour = getHslColourInRange({
          value,
          maximumValue: maximumColourValue,
          minimumValue: minimumColourValue,
          startHue,
          endHue,
        });
      } else {
        opts.colour = getColourInRange({
          value,
          maximumValue: maximumColourValue,
          minimumValue: minimumColourValue,
          startColour: startColour || error,
          endColour: endColour || success,
        });
      }
    } else if (colourProperty.type === PropertyType.Boolean) {
      opts.colour = value === true ? startColour : endColour;
    }
  }

  const markerData = {
    title,
    tooltip,
    isSelected: false,
    colour: opts.colour,
    id: device.$id,
    data: opts,
    minimumValue: minimumColourValue,
    maximumValue: maximumColourValue,
  };

  if ((useColour && colourProperty) || (colourProperty && colourProperty.name === '$kpi')) {
    markerData.colourValue = device[colourProperty.name];
  }

  // eslint-disable-next-line new-cap
  const marker = L.marker(new L.latLng(latitude, longitude), markerData);

  marker.originalIcon = getMarkerIcon(opts);
  marker.selectedIcon = getMarkerIcon(opts, true);
  marker.setIcon(marker.originalIcon);

  return marker;
}

export function bulkGenerateMarkers(dataArray, args, markerStore = []) {
  const batchSize = 250;
  const pending = dataArray.splice(0, batchSize);
  return delay(50).then(() => {
    markerStore = markerStore.concat(pending // eslint-disable-line no-param-reassign
      .map((m) => generateMarker(m, args))
      .filter((m) => m)); // markers without lat/lng are null.
    if (dataArray.length) {
      return bulkGenerateMarkers(dataArray, args, markerStore);
    }
    return Promise.resolve(markerStore);
  });
}

function getNumericEnumValue(enumerations, value) {
  const [en] = enumerations.filter((e) => e.label === value);
  if (!en) {
    return null;
  }
  const enumValue = Number.parseFloat(en.value);
  if (Number.isNaN(enumValue)) {
    return null;
  }
  return enumValue;
}

function getAverageColour(markers, settings = {}) {
  const { colourProperty } = settings;
  const {
    start: startColour,
    end: endColour,
  } = settings.colouring;

  if (colourProperty?.type === PropertyType.Number && colourProperty?.isEnumerated === false) {
    const useHsl = startColour && startColour.substring(0, 3) === 'hsl';
    let maximumValue;
    let minimumValue;
    let average = 0;

    markers.forEach((m) => {
      if (m.options.colourValue === undefined) return;
      ({ maximumValue } = m.options);
      ({ minimumValue } = m.options);
      const { colourValue } = m.options;

      if (colourValue && typeof colourValue === 'number' && !Number.isNaN(colourValue)) {
        average += colourValue;
      }
    });
    average /= markers.length;

    if (useHsl) {
      const parsedStart = parseInt(startColour.substring(4), 10);
      const parsedEnd = parseInt(endColour.substring(4), 10);
      const startHue = Number.isNaN(parsedStart) ? 0 : parsedStart;
      const endHue = Number.isNaN(parsedEnd) ? 240 : parsedEnd;

      return getHslColourInRange({
        value: average,
        maximumValue,
        minimumValue,
        startHue,
        endHue,
      });
    }

    const { error, success } = store.getters[palette];
    return getColourInRange({
      value: average,
      maximumValue,
      minimumValue,
      startColour: startColour || error,
      endColour: endColour || success,
    });
  }

  // Non-numeric colour merging
  let mergedColour = null;
  let foundColours = 0;
  markers.forEach((m) => {
    const options = m.data || m.options;
    const currColour = options.colour;
    if (!currColour) {
      return;
    }
    foundColours += 1;

    if (!mergedColour) {
      mergedColour = Color(currColour);
    } else {
      mergedColour = mergedColour.mix(Color(currColour));
    }
  });

  if (foundColours === 0) {
    mergedColour = getEmptyColour();
  }

  return mergedColour;
}

function getMinColour(markers, colourProperty) {
  let minimumColour = null;
  let minimumValue = Infinity;
  let foundColours = 0;

  markers.forEach((m) => {
    const currColour = m.options.colour;
    if (!currColour || m.options.colourValue === null || m.options.colourValue === undefined) {
      return;
    }
    foundColours += 1;

    let { colourValue } = m.options;
    if (colourProperty.isEnumerated) {
      colourValue = getNumericEnumValue(colourProperty.enumerations, colourValue);
    }

    if (colourValue < minimumValue) {
      minimumValue = colourValue;
      minimumColour = m.options.colour;
    }
  });

  if (foundColours === 0) {
    minimumColour = getEmptyColour();
  }
  return minimumColour;
}

function getMaxColour(markers, colourProperty) {
  let maximumColour = null;
  let maximumValue = -Infinity;
  let foundColours = 0;

  markers.forEach((m) => {
    const currColour = m.options.colour;
    if (!currColour || m.options.colourValue === null || m.options.colourValue === undefined) {
      return;
    }
    foundColours += 1;

    let { colourValue } = m.options;
    if (colourProperty.isEnumerated) {
      colourValue = getNumericEnumValue(colourProperty.enumerations, colourValue);
    }

    if (colourValue !== null && colourValue > maximumValue) {
      maximumValue = colourValue;
      maximumColour = m.options.colour;
    }
  });

  if (foundColours === 0) {
    maximumColour = getEmptyColour();
  }
  return maximumColour;
}

function getClusterOpacity(markers) {
  let count = 0;

  markers.forEach((m) => {
    if (m.options.colourValue === null || m.options.colourValue === undefined) {
      return;
    }
    count += 1;
  });

  if (count === 0) {
    return 0.5;
  }
  return 1;
}

export function generateClusterMarker(group, settings) {
  const markers = group.getAllChildMarkers();
  let clusterType = 'average';
  let colour;
  let opacity = 1;

  if (settings && settings.colouring) {
    clusterType = settings.colouring.cluster || 'average';
  }

  if (settings && settings.$kpi) {
    let average = 0;
    markers.forEach((m) => {
      if (m.options.colourValue === undefined) return;
      const { colourValue: { rawValue } } = m.options;
      average += rawValue;
    });
    average /= markers.length;

    const { $kpi } = settings;
    const boundaries = $kpi.getBoundaries();
    opacity = 1;
    if (boundaries) {
      colour = getBoundaryColour(
        average,
        boundaries,
        $kpi.isPositiveStatus(),
        null,
        false,
        store.getters[`${COLOURS}/${PALETTE_COLOURS}`],
      );
    } else {
      colour = store.getters[palette].primary;
    }
  } else if (settings.useColour && markers !== undefined && markers.length > 0) {
    const { clusterOpacity } = settings?.colouring || {};

    const { colourProperty } = settings;

    switch (clusterType) {
      case 'average':
        colour = getAverageColour(markers, settings);
        // opacity = getAverageOpacity(markers);
        opacity = clusterOpacity || getClusterOpacity(markers);
        break;
      case 'min':
        colour = getMinColour(markers, colourProperty);
        opacity = clusterOpacity || getClusterOpacity(markers);
        break;
      case 'max':
        colour = getMaxColour(markers, colourProperty);
        opacity = clusterOpacity || getClusterOpacity(markers);
        break;
      default: break;
    }
  }

  return getMarkerIcon({
    text: markers.length,
    colour,
    opacity,
  });
}

export function parseLocations(devices, longitudeProperty, latitudeProperty, state = {}) {
  const batch = devices.splice(0, 1000);
  const located = batch.reduce((s, d) => {
    const longitude = Number.parseFloat(d[longitudeProperty]);
    const latitude = Number.parseFloat(d[latitudeProperty]);
    if (Number.isNaN(longitude) || Number.isNaN(latitude)) {
      return s;
    }
    s[d.$id] = { $id: d.$id, longitude, latitude }; // eslint-disable-line no-param-reassign
    return s;
  }, state);
  if (devices.length) {
    return delay(1)
      .then(() => parseLocations(devices, longitudeProperty, latitudeProperty, located));
  }
  return Promise.resolve(located);
}

function getLongitudeLatitudeFromArgs(args) {
  const longitudeProperty = args.longitudeProperty
    ? args.longitudeProperty.name || args.longitudeProperty
    : 'longitude';
  const latitudeProperty = args.latitudeProperty
    ? args.latitudeProperty.name || args.latitudeProperty
    : 'latitude';

  return { longitudeProperty, latitudeProperty };
}

export function locateDevices(devices, args) {
  const { longitudeProperty, latitudeProperty } = getLongitudeLatitudeFromArgs(args);
  return parseLocations(Object.values(devices), longitudeProperty, latitudeProperty);
}

export async function locateDevice(device, args) {
  const { longitudeProperty, latitudeProperty } = getLongitudeLatitudeFromArgs(args);
  const locations = await parseLocations([device], longitudeProperty, latitudeProperty);
  return locations[device.$id];
}

export const VIABLE_LATITUDES = ['latitude', 'lat', 'y'];
export const VIABLE_LONGITUDES = ['longitude', 'lon', 'lng', 'x'];

export const findMapPropertiesInObject = (obj) => {
  const keys = Object.keys(obj);
  const latitudeProperty = VIABLE_LATITUDES.find((lat) => keys.includes(lat));
  const longitudeProperty = VIABLE_LONGITUDES.find((lng) => keys.includes(lng));
  return { latitudeProperty, longitudeProperty };
};

export function findLatLngProperties(properties) {
  const props = { lat: 'latitude', lng: 'longitude' };

  // We want to get the first ones
  let foundLat = false;
  let foundLng = false;

  properties.forEach((prop) => {
    const { field } = prop;

    if (!foundLat && VIABLE_LATITUDES.includes(field.toLowerCase())) {
      foundLat = true;
      props.lat = field;
    } else if (!foundLng && VIABLE_LONGITUDES.includes(field.toLowerCase())) {
      foundLng = true;
      props.lng = field;
    }
  });
  return props;
}
